import { Badge } from '@/ui/Badge';
import { Card } from '@/ui/Card';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import { estadoDelPorcentaje, useCultura, useTendenciaDeCultura } from './api';
import {
  FilaDeArea, GraficoDeMeses, Indicador, Indicadores, ListaDeAlertas, Titulo,
} from './piezas';

/**
 * Cultura — participación, publicaciones, reconocimientos y moderación.
 *
 * ## Lo que cambia
 *
 * - **Los iconos de «Valores más reconocidos» se pintan.** El vanilla ponía
 *   `${v.icon}` como texto, así que en pantalla salía «bi-star» escrito al lado
 *   del nombre del valor.
 * - El resumen y la tendencia son dos consultas con su propio estado, como en
 *   Aprendizaje.
 */
export function Cultura({ activa }: { activa: boolean }) {
  const resumen = useCultura(activa);
  const tendencia = useTendenciaDeCultura(activa);
  const d = resumen.data;

  if (resumen.isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (resumen.isError) {
    return (
      <EstadoError
        err={resumen.error}
        que="la analítica de cultura"
        onReintentar={() => void resumen.refetch()}
      />
    );
  }
  if (!d) return null;

  const o = d.overview;
  const masInteracciones = Math.max(1, ...d.by_area.map((a) => a.interactions));
  const masPublicaciones = Math.max(1, ...d.posts_by_type.map((t) => t.count));

  return (
    <div className="flex flex-col gap-6">
      <Indicadores>
        <Indicador
          icono="bi-people-fill"
          estado={estadoDelPorcentaje(o.participation_rate, 60, 30)}
          valor={`${o.participation_rate} %`}
          etiqueta="Participación"
          nota={`${o.participating_users} de ${o.active_users} colaboradores`}
        />
        <Indicador
          icono="bi-chat-square-text-fill" valor={o.total_posts} etiqueta="Publicaciones"
          nota={`${o.total_comments} comentarios`}
        />
        <Indicador icono="bi-hand-thumbs-up-fill" valor={o.total_reactions} etiqueta="Reacciones" />
        <Indicador icono="bi-award-fill" valor={o.total_kudos} etiqueta="Reconocimientos" />
        <Indicador
          icono="bi-people" valor={o.communities} etiqueta="Comunidades"
          nota={`${o.celebrations_attended} felicitaciones`}
        />
        <Indicador
          icono="bi-flag-fill" estado={o.pending_reports ? 'malo' : null}
          valor={o.pending_reports} etiqueta="Reportes pendientes" nota="moderación"
        />
      </Indicadores>

      <section aria-labelledby="cul-alertas">
        <Titulo icono="bi-bell-fill"><span id="cul-alertas">Alertas</span></Titulo>
        <ListaDeAlertas alertas={d.alerts} />
      </section>

      <section aria-labelledby="cul-tendencia">
        <Titulo icono="bi-bar-chart-line-fill"><span id="cul-tendencia">Actividad</span></Titulo>
        <Card className="p-4">
          {tendencia.isPending ? (
            <Skeleton className="h-44 w-full" />
          ) : tendencia.isError ? (
            <EstadoError
              err={tendencia.error}
              que="la actividad"
              onReintentar={() => void tendencia.refetch()}
            />
          ) : (
            <GraficoDeMeses
              titulo={`Publicaciones y reconocimientos de los últimos ${tendencia.data?.series.length ?? 0} meses`}
              nombreA="Publicaciones"
              nombreB="Reconocimientos"
              serie={(tendencia.data?.series ?? []).map((s) => ({
                label: s.label, a: s.posts, b: s.kudos,
              }))}
            />
          )}
        </Card>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-labelledby="cul-areas">
          <Titulo icono="bi-diagram-2-fill"><span id="cul-areas">Participación por área</span></Titulo>
          {d.by_area.length ? (
            <Card className="p-4">
              <ul className="flex flex-col gap-3">
                {d.by_area.map((a) => (
                  <FilaDeArea
                    key={a.area}
                    nombre={a.area}
                    pct={Math.round((a.interactions / masInteracciones) * 100)}
                    derecha={<>{a.interactions} <span className="font-normal text-muted">({a.avg}/persona)</span></>}
                  />
                ))}
              </ul>
            </Card>
          ) : (
            <EstadoVacio compacto icono="bi-diagram-2" titulo="Sin actividad todavía" />
          )}
        </section>

        <div className="flex flex-col gap-6">
          <section aria-labelledby="cul-tipos">
            <Titulo icono="bi-collection-fill"><span id="cul-tipos">Publicaciones por tipo</span></Titulo>
            {d.posts_by_type.length ? (
              <Card className="p-4">
                <ul className="flex flex-col gap-3">
                  {d.posts_by_type.map((t) => (
                    <FilaDeArea
                      key={t.label}
                      nombre={t.label}
                      pct={Math.round((t.count / masPublicaciones) * 100)}
                      derecha={t.count}
                    />
                  ))}
                </ul>
              </Card>
            ) : (
              <EstadoVacio compacto icono="bi-chat-square" titulo="Sin publicaciones" />
            )}
          </section>

          <section aria-labelledby="cul-valores">
            <Titulo icono="bi-trophy-fill"><span id="cul-valores">Valores más reconocidos</span></Titulo>
            {d.top_values.length ? (
              <ul className="flex flex-wrap gap-2">
                {d.top_values.map((v) => (
                  <li key={v.name}>
                    <Badge tone="warn">
                      <Icono nombre={v.icon} respaldo="bi-star" /> {v.name} · {v.count}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-3 text-muted">Sin reconocimientos todavía.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
